import { AppWindow, ArrowRight, Blocks, Code2, FileJson, Loader2, Plus, Sparkles, Upload } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type IntegrationDraftListPage } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { LoadError } from '@/components/ui/load-state';
import { Textarea } from '@/components/ui/textarea';
import { handbookUrl } from '@/handbook/handbookUrls';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';

import { AppIntegrationReviews } from './AppIntegrationReviews';
import {
  DRAFT_LIST_PAGE_SIZE,
  type DraftListFilter,
  type DraftListView,
  draftListEmptyMessage,
  draftListFilterOptions,
  draftListSearchParams,
  readDraftListView,
  statusMetaFor,
  errorMessage,
  rememberGeneration,
  relativeTime,
  slugifyIntegrationId,
} from './builderModel';
import { Field, IntegrationLogo } from './builderUi';

const GENERATION_STAGES = ['Reading the spec…', 'Choosing operations…', 'Writing the copy…', 'Translating…'];

function GenerateForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [spec, setSpec] = useState('');
  const [fileName, setFileName] = useState<string>();
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!generating) return undefined;
    const timer = window.setInterval(() => setStage((current) => Math.min(current + 1, GENERATION_STAGES.length - 1)), 6000);
    return () => window.clearInterval(timer);
  }, [generating]);

  const loadFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('The spec is larger than 2 MB.');
      return;
    }
    setSpec(await file.text());
    setFileName(file.name);
    setError(undefined);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!spec.trim() || description.trim().length < 10) return;
    setGenerating(true);
    setStage(0);
    setError(undefined);
    try {
      const draft = await adminClient.generateIntegrationDraft({ spec, description: description.trim() });
      rememberGeneration(draft.draftId, draft.generation);
      navigate(`/integrations/builder/${draft.draftId}?step=review`);
    } catch (failure) {
      setError(errorMessage(failure));
      setGenerating(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-5">
      <Field
        id="generate-spec"
        label="OpenAPI spec"
        hint={fileName ? `Loaded ${fileName}.` : 'JSON or YAML, OpenAPI 3 or Swagger 2, bundled into one file (2 MB max).'}
      >
        <div className="space-y-2">
          <Textarea
            id="generate-spec"
            value={spec}
            onChange={(event) => {
              setSpec(event.target.value);
              setFileName(undefined);
            }}
            placeholder="Paste the spec, or choose a file below"
            className="min-h-32 font-mono text-[11px]"
            spellCheck={false}
          />
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-brand">
            <Upload className="h-3.5 w-3.5" />
            Choose a file
            <input type="file" accept=".json,.yaml,.yml,application/json,text/yaml" className="sr-only" onChange={(event) => void loadFile(event.target.files?.[0])} />
          </label>
        </div>
      </Field>
      <Field
        id="generate-description"
        label="What is it for?"
        hint="A few sentences: what the service is, what agency apps should do with it, whose account it uses (one RapidSOS account, each agency's, or each person's), and prices if you know them."
      >
        <Textarea
          id="generate-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Acme CRM holds each agency's community contacts. Apps should search and add contacts. Each agency connects its own Acme admin account."
          className="min-h-24 text-sm"
          maxLength={4000}
        />
      </Field>
      {error ? <div className="rounded-lg border border-destructive-border bg-destructive-bg px-3 py-2 text-sm text-destructive-foreground">{error}</div> : null}
      <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
        <span className="text-xs text-muted-foreground" aria-live="polite">
          {generating ? GENERATION_STAGES[stage] : 'You review and edit everything before it goes to review.'}
        </span>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={generating}>
            Cancel
          </Button>
          <Button type="submit" disabled={!spec.trim() || description.trim().length < 10 || generating}>
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Generate draft
          </Button>
        </div>
      </div>
    </form>
  );
}

function BlankForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const id = idTouched ? integrationId : slugifyIntegrationId(title);
  const idValid = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(id) && id.length <= 48;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !idValid) return;
    setCreating(true);
    try {
      const draft = await adminClient.createIntegrationDraft({ integrationId: id, title: title.trim() });
      navigate(`/integrations/builder/${draft.draftId}?step=contract`);
    } catch (error) {
      toast.error(errorMessage(error));
      setCreating(false);
    }
  };

  return (
    <form id="new-integration" onSubmit={(event) => void submit(event)} className="space-y-5">
      <Field id="new-title" label="Name">
        <Input id="new-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Acme Directory" maxLength={80} />
      </Field>
      <Field
        id="new-id"
        label="Integration id"
        hint={id && !idValid ? <span className="text-destructive">Lowercase letters, digits and single dashes, starting with a letter.</span> : 'Permanent. It names the vault path and the catalog entry.'}
      >
        <Input
          id="new-id"
          value={id}
          onChange={(event) => {
            setIdTouched(true);
            setIntegrationId(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
          }}
          placeholder="acme-directory"
          className="font-mono text-sm"
        />
      </Field>
      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!title.trim() || !idValid || creating}>
          {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Create and import spec
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </form>
  );
}

function NewDraftDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<'generate' | 'blank'>('generate');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New no-code integration"
      description={
        mode === 'generate'
          ? 'Give it the OpenAPI spec and a description. AI drafts the operations, copy in every language, keywords, connection and pricing; you review every step.'
          : "Name it. Next you'll import its OpenAPI spec; everything else can be changed until you submit it for review."
      }
      className="max-w-2xl"
    >
      <div className="mb-5 inline-flex rounded-lg border border-border p-0.5 text-xs" role="tablist" aria-label="How to start">
        {(
          [
            { id: 'generate', label: 'Generate with AI', icon: Sparkles },
            { id: 'blank', label: 'Start blank', icon: FileJson },
          ] as const
        ).map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={mode === tab.id}
            onClick={() => setMode(tab.id)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors',
              mode === tab.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>
      {mode === 'generate' ? <GenerateForm onClose={onClose} /> : <BlankForm onClose={onClose} />}
    </Dialog>
  );
}

export function IntegrationBuilderListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = readDraftListView(searchParams);
  const [query, setQuery] = useState(view.q);
  const debouncedQuery = useDebouncedValue(query);
  const [listing, setListing] = useState<IntegrationDraftListPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const latest = useRef(0);

  const { canApproveIntegrations } = useAdminAccess();
  const items = listing?.items;
  const offset = (view.page - 1) * DRAFT_LIST_PAGE_SIZE;

  const showView = useCallback(
    (next: DraftListView) => setSearchParams(draftListSearchParams(next), { replace: true }),
    [setSearchParams],
  );

  const [shownQuery, setShownQuery] = useState(view.q);
  if (shownQuery !== view.q) {
    setShownQuery(view.q);
    if (view.q !== debouncedQuery.trim()) setQuery(view.q);
  }

  const lastDebounced = useRef(debouncedQuery);
  useEffect(() => {
    if (lastDebounced.current === debouncedQuery) return;
    lastDebounced.current = debouncedQuery;
    if (debouncedQuery.trim() !== view.q) showView({ q: debouncedQuery, status: view.status, page: 1 });
  }, [debouncedQuery, view.q, view.status, showView]);

  useEffect(() => {
    const request = ++latest.current;
    setLoading(true);
    setError(undefined);
    adminClient
      .listIntegrationDrafts({
        limit: DRAFT_LIST_PAGE_SIZE,
        offset,
        q: view.q.trim() || undefined,
        status: view.status === 'all' ? undefined : view.status,
      })
      .then((response) => {
        if (request !== latest.current) return;
        if (response.items.length === 0 && response.total > 0 && offset > 0) {
          showView({ q: view.q, status: view.status, page: Math.ceil(response.total / DRAFT_LIST_PAGE_SIZE) });
          return;
        }
        setListing(response);
      })
      .catch((failure) => {
        if (request === latest.current) setError(errorMessage(failure));
      })
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
  }, [offset, view.q, view.status, reloadKey, showView]);

  const load = () => setReloadKey((key) => key + 1);
  const emptyMessage = draftListEmptyMessage(view);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Integration Builder"
        description="Bring a new service into Studio so generated apps can use it: no-code from an OpenAPI spec, or an SDK package when it needs custom signing, streaming or a vendor SDK."
        actions={
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" asChild>
              <a href={handbookUrl('/handbook/integrations/sdk-tutorial')} target="_blank" rel="noreferrer" aria-label="SDK tutorial">
                <Code2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">SDK tutorial</span>
              </a>
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New integration
            </Button>
          </div>
        }
      />

      <NewDraftDialog key={creating ? 'open' : 'closed'} open={creating} onClose={() => setCreating(false)} />

      {canApproveIntegrations ? <AppIntegrationReviews /> : null}

      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Drafts</h2>
        {listing ? <span className="text-xs text-muted-foreground">{listing.total} total</span> : null}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput value={query} onChange={setQuery} placeholder="Search name or integration id" label="Search integrations" />
        <SegmentedFilter<DraftListFilter>
          label="Status"
          value={view.status}
          onChange={(status) => showView({ q: query, status, page: 1 })}
          options={draftListFilterOptions(listing?.statusCounts)}
        />
      </div>

      {error ? <LoadError message={error} onRetry={load} className="mb-3 rounded-xl border border-border" /> : null}
      {!items && !error ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading drafts…
        </div>
      ) : null}
      {items && items.length === 0 && emptyMessage ? (
        <div className="rounded-xl border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      ) : null}
      {items && items.length === 0 && !emptyMessage ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-14 text-center">
          <Blocks className="h-6 w-6 text-muted-foreground" />
          <div className="text-sm font-medium">No drafts yet</div>
          <p className="max-w-sm text-xs leading-5 text-muted-foreground">Start with an OpenAPI spec for the service you want generated apps to call.</p>
        </div>
      ) : null}
      {items && items.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-card/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Integration</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="hidden px-4 py-2.5 font-medium md:table-cell">Operations</th>
                <th className="hidden px-4 py-2.5 font-medium md:table-cell">Checklist</th>
                <th className="px-4 py-2.5 text-right font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.draftId} className="border-t border-border transition-colors hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link to={`/integrations/builder/${item.draftId}`} className="flex items-center gap-3">
                      <IntegrationLogo src={item.logoDataUrl} color={item.brandColor} title={item.title} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.title}</span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">{item.integrationId}</span>
                        {item.app ? (
                          <span className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                            <AppWindow className="h-3 w-3" />
                            From the Studio app {item.app.appName || item.app.name}
                            <span className={cn('rounded-full border px-1.5', item.app.offeredToEveryone ? 'border-brand/40 text-brand' : 'border-border')}>
                              {item.app.deleted ? 'Deleted by its owners' : item.app.offeredToEveryone ? 'Offered to everyone' : 'Its organization only'}
                            </span>
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill tone={statusMetaFor(item).tone}>{statusMetaFor(item).label}</StatusPill>
                  </td>
                  <td className="hidden px-4 py-3 font-mono text-xs md:table-cell">{item.operationCount || '—'}</td>
                  <td className="hidden px-4 py-3 text-xs md:table-cell">
                    {item.readyForReview ? <span className="text-success">Complete</span> : <span className="text-muted-foreground">In progress</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                    {relativeTime(item.updatedAt)}
                    <div className="truncate text-[11px]">{item.updatedBy}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {listing ? (
            <TablePager
              total={listing.total}
              limit={DRAFT_LIST_PAGE_SIZE}
              offset={offset}
              disabled={loading}
              onOffsetChange={(next) => showView({ q: view.q, status: view.status, page: Math.floor(next / DRAFT_LIST_PAGE_SIZE) + 1 })}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
